/**
 * An API failure carrying the machine-readable code from the backend.
 *
 * `code` is the contract, not `message`. Two rejections that both answer 409 -
 * "slot is full" and "period is closed", say - can only be told apart by this
 * code. The wording of `message` may change at any time without notice, so
 * never branch on it.
 */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>
  readonly correlationId?: string
  readonly requestId?: string

  constructor(init: {
    message: string
    code: string
    status: number
    details?: Record<string, unknown>
    correlationId?: string
    requestId?: string
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.code = init.code
    this.status = init.status
    this.details = init.details
    this.correlationId = init.correlationId
    this.requestId = init.requestId
  }

  /** Extra context already narrowed to a number, e.g. `currentRevision`. */
  detailNumber(key: string): number | undefined {
    const value = this.details?.[key]
    return typeof value === 'number' ? value : undefined
  }

  /** Extra context already narrowed to an array, e.g. per-field `violations`. */
  detailArray(key: string): unknown[] | undefined {
    const value = this.details?.[key]
    return Array.isArray(value) ? value : undefined
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/** True when the failure carries one of the given codes. */
export function hasErrorCode(error: unknown, ...codes: string[]): boolean {
  return isApiError(error) && codes.includes(error.code)
}
